import type { InputHTMLAttributes, ReactNode } from "react";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  icon?: ReactNode;
}

/**
 * Reusable form input with integrated label, hint text, error state,
 * and optional leading icon.
 */
export function Input({
  label,
  hint,
  error,
  icon,
  id,
  className = "",
  ...props
}: InputProps) {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, "-") : undefined);

  return (
    <div className={`form-group ${className}`}>
      {label ? (
        <label className="form-label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      {hint && !error ? (
        <span className="form-hint">{hint}</span>
      ) : null}
      <div style={{ position: "relative" }}>
        {icon ? (
          <span
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--z-text-secondary)",
              display: "flex",
              pointerEvents: "none",
            }}
          >
            {icon}
          </span>
        ) : null}
        <input
          id={inputId}
          className={`form-input ${error ? "form-input--error" : ""}`}
          style={icon ? { paddingLeft: "40px" } : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...props}
        />
      </div>
      {error ? (
        <div className="form-error" id={`${inputId}-error`} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
